import { getCurrencySymbol } from '@components/transactions/util';
import { AwardIcon } from '@icons/Award';
import { cn } from '@lib/utils';
import { routesMap } from '@shared/lib';
import { Button } from '@ui/button';
import { Link } from 'react-router-dom';
import { Tariff } from '../model/usePlans';

interface Props {
  isDefault: boolean;
  isActive: boolean;
  isBestOffer: boolean;
  className?: string;
  isPromoCard?: boolean;
  isPricing?: boolean;
  tariff: Tariff;
}

const CARD_TEXTS = {
  downgrade: 'Downgrade',
  currentPlan: 'Current Plan',
};

const getPeriodPrise = (tariff: Tariff) => {
  switch (tariff.period) {
    case 'MONTH':
      return 'Billed monthly';
    case 'YEAR':
      return `Billed annually at ${getCurrencySymbol(tariff.currency)}${tariff.cost}/year`;
    case 'QUARTER':
      return `Billed quarterly at ${getCurrencySymbol(tariff.currency)}${tariff.cost}/quart`;
    case 'HALF_YEAR':
      return `Billed for six months at ${getCurrencySymbol(tariff.currency)}${tariff.cost}/six months`;
  }
};

export const PlanCard = ({
  isDefault,
  isActive,
  isBestOffer,
  className,
  isPromoCard = true,
  isPricing,
  tariff,
}: Props) => {
  const rootClasses = isDefault ? 'bg-neutral08' : 'bg-neutral02';
  const nameClasses = isDefault ? 'text-neutral04' : 'text-neutral08';
  const priceClasses = isDefault ? 'text-neutral02' : 'text-neutral08';
  const priceInfoClasses = isDefault ? 'text-neutral04' : 'text-neutral05';
  const dividerClasses = isDefault
    ? 'xl:my-[18px] my-[18px]'
    : 'xl:my-[16px] my-[16px]';
  const descriptionClasses = isDefault
    ? 'mb-[18px] text-neutral04'
    : 'mb-[16px] text-neutral05';
  const buttonClasses = isDefault
    ? ''
    : 'bg-black disabled:bg-neutral02 disabled:text-neutral04';

  const getPlanText = `Get ${name}`;

  const defaultButtonText = isActive
    ? CARD_TEXTS.currentPlan
    : CARD_TEXTS.downgrade;
  const notDefaultButtonText = isActive ? CARD_TEXTS.currentPlan : getPlanText;
  const buttonText = isDefault ? defaultButtonText : notDefaultButtonText;

  const formattedCurrency = getCurrencySymbol(tariff.currency);

  return (
    <div className={cn('relative z-0', { 'pt-16': isBestOffer }, className)}>
      {isBestOffer && (
        <div className="absolute left-0 right-0 top-0 flex w-full items-center gap-[4px] rounded-[32px_32px_0px_0px] border-2 border-[rgba(255,255,255,0.24)] bg-[rgba(17,19,21,0.95)] px-7 pb-12 pt-6 backdrop-blur-xl">
          <span className="inline-block rounded-full text-[12px] font-medium leading-[150%] text-white">
            Best for you!
          </span>
          <span className="inline-block rounded-full text-[12px] font-medium leading-[150%] text-[#6F767E] ">
            Based on your expenses
          </span>
        </div>
      )}
      <div
        className={cn(
          'relative z-10 flex h-full grid-cols-[1fr_1px_1fr] flex-col items-center rounded-[32px] border-white bg-opacity-90 px-7 pb-6 pt-7 font-medium backdrop-blur-xl  xl:flex xl:flex-col xl:border-[2px]',
          rootClasses,
          {
            'lg:h-[128px] xl:h-[287px]': isPromoCard,
            'lg:grid lg:w-auto lg:flex-row': isPricing,
          },
        )}
      >
        <div className="w-full xl:pr-0">
          <div className="relative mb-7 flex h-[14px] w-full justify-between">
            <p
              className={cn(
                'text-xl leading-[14px] lg:text-[20px]',
                nameClasses,
              )}
            >
              {tariff.name}
            </p>
            {!isDefault && (
              <AwardIcon
                className={cn('absolute -right-4 -top-4 xl:-right-4', {
                  'lg:right-4': isPricing,
                })}
              />
            )}
          </div>
          <p className="flex items-baseline gap-2 lg:items-center xl:items-baseline">
            <div
              className={cn(
                'text-4xl font-medium leading-[25px] text-neutral02',
                priceClasses,
              )}
            >
              {formattedCurrency}
              {tariff.monthlyPrice}
            </div>
            <div
              className={cn(
                'flex flex-col text-base font-medium leading-none',
                priceInfoClasses,
              )}
            >
              /mo
              <p
                className={cn(
                  'hidden flex-col text-xs font-medium leading-[8px] sm:text-sm lg:flex xl:hidden',
                  priceInfoClasses,
                )}
              >
                {getPeriodPrise(tariff)}
              </p>
            </div>
          </p>
          <p
            className={cn(
              'mt-3 flex flex-col text-xs font-medium leading-[8px] sm:text-sm lg:hidden xl:flex',
              priceInfoClasses,
            )}
          >
            {getPeriodPrise(tariff)}
          </p>
          <p
            className={cn(
              'flex flex-col text-xs font-medium leading-none sm:text-sm lg:hidden xl:block',
              priceInfoClasses,
            )}
          >
            or you could pay less &mdash; see details
          </p>
        </div>
        {isPromoCard && (
          <div
            className={cn(
              'h-px w-full bg-card-stroke bg-opacity-40 lg:m-0 lg:h-[76px] xl:h-px xl:min-h-px xl:w-full',
              dividerClasses,
            )}
          />
        )}
        {isPromoCard && (
          <div className="flex h-full w-full flex-col lg:pl-9 xl:h-full xl:p-0">
            <div className="flex flex-grow flex-col lg:w-auto">
              {(tariff as any).description && (
                <p className={cn(descriptionClasses)}>
                  {(tariff as any).description}
                </p>
              )}
              <Button
                className={cn('mt-auto w-fit', buttonClasses)}
                variant="secondary"
                disabled={isActive}
                asChild
              >
                {isActive ? (
                  <div className="pointer-events-none">{buttonText}</div>
                ) : (
                  <Link
                    to={routesMap.planActivation.full.replace(
                      ':planType',
                      tariff.id,
                    )}
                  >
                    {buttonText}
                  </Link>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
